import * as React from "react";
import { RadioGroupItem } from "./radio-group";
import { cn } from "../../lib/utils";

/**
 * RadioItem - A card-based radio button component
 *
 * Designed for use within a RadioGroup to create visually rich radio selections
 * with icons and labels. When selected, the item displays a primary background.
 *
 * @example
 * ```tsx
 * <RadioGroup className="grid grid-cols-3 gap-3" value={value} onValueChange={setValue}>
 *   <RadioItem id="card" value="card" label="Card">
 *     <CreditCard />
 *   </RadioItem>
 *   <RadioItem id="paypal" value="paypal" label="PayPal">
 *     <Wallet />
 *   </RadioItem>
 *   <RadioItem id="apple" value="apple" label="Apple Pay">
 *     <Apple />
 *   </RadioItem>
 * </RadioGroup>
 * ```
 */

export interface RadioItemProps extends React.ComponentPropsWithoutRef<"div"> {
  /**
   * The value associated with this radio item
   */
  value: string;
  /**
   * The unique ID for this radio item
   */
  id: string;
  /**
   * The label text to display below the icon
   */
  label: string;
  /**
   * Icon or content to display (typically a Lucide icon component)
   */
  children?: React.ReactNode;
  /**
   * Size of the icon (defaults to 20)
   */
  iconSize?: number;
}

export const RadioItem = React.forwardRef<HTMLDivElement, RadioItemProps>(
  ({ className, value, id, label, children, iconSize = 20, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "relative flex cursor-pointer flex-col items-center gap-1 rounded-md border border-input px-0 py-1 text-center shadow-xs outline-none",
          "transition-all duration-200",
          // Selected state - primary background and border
          "has-data-checked:border-primary has-data-checked:bg-primary has-data-checked:text-primary-foreground",
          // Focus state
          "has-focus-visible:border-ring has-focus-visible:ring-[3px] has-focus-visible:ring-ring/50",
          className
        )}
        {...props}
      >
        <RadioGroupItem className="sr-only" id={id} value={value} />
        {children && (
          <div
            aria-hidden="true"
            className="opacity-60 transition-opacity has-data-checked:opacity-100"
            style={{ width: iconSize, height: iconSize }}
          >
            {React.isValidElement(children)
              ? React.cloneElement(
                  children as React.ReactElement<{ size?: number; className?: string }>,
                  {
                    size: iconSize,
                    className: cn(
                      (children as React.ReactElement<{ className?: string }>).props?.className
                    ),
                  }
                )
              : children}
          </div>
        )}
        <label
          className={cn(
            "cursor-pointer font-medium text-xs leading-none",
            "after:absolute after:inset-0"
          )}
          htmlFor={id}
        >
          {label}
        </label>
      </div>
    );
  }
);

RadioItem.displayName = "RadioItem";
